<template>
  <div class="rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-slate-900/30 backdrop-blur-md shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.35),0_8px_24px_rgba(0,0,0,0.4)] p-6 mb-6">
    <h3 class="text-base font-semibold text-heading mb-1">
      {{ $t('AI_SETTINGS.DATA_SYNC.TITLE') }}
    </h3>
    <p class="text-sm text-body-light mb-4">
      {{ $t('AI_SETTINGS.DATA_SYNC.DESCRIPTION') }}
    </p>

    <div class="grid grid-cols-1 gap-4">
      <!-- Help Section -->
      <div>
        <button
          class="text-sm font-medium text-primary-500 hover:underline"
          @click="showHelp = !showHelp"
        >
          {{ showHelp ? $t('AI_SETTINGS.DATA_SYNC.HIDE_HELP') : $t('AI_SETTINGS.DATA_SYNC.SHOW_HELP') }}
        </button>
        <div
          v-if="showHelp"
          class="mt-3 p-4 rounded-lg border border-white/15 dark:border-white/10 bg-white/70 dark:bg-slate-900/40 text-sm text-body"
        >
          <ol class="list-decimal ml-5 space-y-1 marker:text-body-light">
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_OPEN_SHEET')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_FIND_ID')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_SHEET_NAME')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_CREATE_SA')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_ENABLE_API')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_SHARE')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_TEST')"></li>
          </ol>
        </div>
      </div>

      <!-- Google Sheets Configuration -->
      <div class="space-y-4 border-t border-white/15 dark:border-white/10 pt-4">
        <div>
          <woot-input
            v-model="form.google_spreadsheet_id"
            :label="$t('AI_SETTINGS.DATA_SYNC.SPREADSHEET_ID')"
            :required="true"
            :placeholder="$t('AI_SETTINGS.DATA_SYNC.SPREADSHEET_ID_HINT')"
            :error="v$.form.google_spreadsheet_id.$error ? getRequiredFieldError('AI_SETTINGS.DATA_SYNC.SPREADSHEET_ID') : ''"
            :class="{ error: v$.form.google_spreadsheet_id.$error }"
            type="text"
            maxlength="200"
            @blur="v$.form.google_spreadsheet_id.$touch"
          />
          <p class="text-xs text-body-light mt-1">
            {{ $t('AI_SETTINGS.DATA_SYNC.SPREADSHEET_ID_HINT') }}
          </p>
        </div>

        <div>
          <woot-input
            v-model="form.google_sheet_name"
            :label="$t('AI_SETTINGS.DATA_SYNC.SHEET_NAME')"
            :required="true"
            :placeholder="$t('AI_SETTINGS.DATA_SYNC.SHEET_NAME_HINT')"
            :error="v$.form.google_sheet_name.$error ? getRequiredFieldError('AI_SETTINGS.DATA_SYNC.SHEET_NAME') : ''"
            :class="{ error: v$.form.google_sheet_name.$error }"
            type="text"
            maxlength="200"
            @blur="v$.form.google_sheet_name.$touch"
          />
          <p class="text-xs text-body-light mt-1">
            {{ $t('AI_SETTINGS.DATA_SYNC.SHEET_NAME_HINT') }}
          </p>
        </div>

        <div>
          <FormLabel
            :label="$t('AI_SETTINGS.DATA_SYNC.SERVICE_ACCOUNT_JSON')"
            :required="true"
            :has-error="v$.form.google_credentials.$error"
          />
          <TextArea
            v-model="form.google_credentials"
            name="google-service-account-json"
            :placeholder="$t('AI_SETTINGS.DATA_SYNC.SERVICE_ACCOUNT_PLACEHOLDER')"
            :message="v$.form.google_credentials.$error ? getRequiredFieldError('AI_SETTINGS.DATA_SYNC.SERVICE_ACCOUNT_JSON') : ''"
            :message-type="v$.form.google_credentials.$error ? 'error' : 'info'"
            auto-height
            :resize="true"
            :min-height="'96px'"
            rows="6"
            :disabled="isTesting"
            custom-text-area-class="font-mono"
            @blur="v$.form.google_credentials.$touch"
          />
          <p class="text-xs text-body-light mt-1">
            {{ $t('AI_SETTINGS.DATA_SYNC.SERVICE_ACCOUNT_HINT') }}
          </p>
          <p class="text-xs text-body-light mt-1">
            {{ $t('AI_SETTINGS.DATA_SYNC.SHARE_HINT') }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center gap-3">
          <woot-button
            :is-loading="isTesting"
            :disabled="!canTest"
             variant="smooth" color-scheme="ghost" size="small"
            @click="onTest"
          >
            {{ $t('AI_SETTINGS.DATA_SYNC.TEST_CONNECTION') }}
          </woot-button>
        </div>

        <!-- Last Sync Info -->
        <div v-if="config.last_synced_at" class="text-xs text-body-light">
          {{ $t('AI_SETTINGS.DATA_SYNC.LAST_SYNCED_AT') }}: {{ humanize(config.last_synced_at) }}
        </div>
      </div>

      <!-- Layout Editor -->
      <div class="border-t border-white/15 dark:border-white/10 pt-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">{{ $t('AI_SETTINGS.DATA_SYNC.LAYOUT.TITLE') }}</span>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-body-light">
                <th class="px-2 py-1">#</th>
                <th class="px-2 py-1">Display name</th>
                <th class="px-2 py-1">Enabled</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(it, idx) in layoutItems" :key="it.internal_key" class="border-t border-white/10">
                <td class="px-2 py-1 w-10">{{ idx + 1 }}</td>
                <td class="px-2 py-1">
                  <input 
                    :value="it.display_name" 
                    @input="updateLayoutItem(idx, 'display_name', $event.target.value)"
                    class="input-base w-full" 
                  />
                </td>
                
                <td class="px-2 py-1">
                  <woot-switch 
                    :model-value="it.enabled" 
                    @input="updateLayoutItem(idx, 'enabled', $event)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Status Display -->
      <div
        class="border-t border-white/15 dark:border-white/10 pt-4"
      >
        <span class="text-xs text-grass-600 dark:text-grass-400 font-medium">
          {{ $t('AI_SETTINGS.DATA_SYNC.STATUS.ENABLED') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import DataSyncConfigAPI from 'dashboard/api/dataSyncConfig';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import FormLabel from 'dashboard/components/widgets/forms/FormLabel.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

export default {
  name: 'GoogleSheetsSyncCard',
  setup() {
    return { v$: useVuelidate() };
  },
  components: {
    FormLabel,
    TextArea,
  },
  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    layoutItems: {
      type: Array,
      default: () => []
    }
  },
  emits: ['config-change', 'layout-change'],
  data() {
    return {
      isTesting: false,
      showHelp: false,
      form: {
        is_enabled: true, // Always enabled when Google Sheets is selected
        google_spreadsheet_id: '',
        google_sheet_name: '',
        google_credentials: '',
      },
    };
  },
  computed: {
    canTest() {
      return (
        Boolean(this.form.google_spreadsheet_id) &&
        Boolean(this.form.google_sheet_name) &&
        Boolean((this.form.google_credentials || '').trim())
      );
    },
  },
  watch: {
    config: {
      handler(newConfig) {
        if (newConfig) {
          this.form.is_enabled = true;
          this.form.google_spreadsheet_id = newConfig.google_spreadsheet_id || '';
          this.form.google_sheet_name = newConfig.google_sheet_name || '';
          this.form.google_credentials = newConfig.google_credentials || '';
        }
      },
      immediate: true
    },
    layoutItems: {
      handler() {
      },
      immediate: true
    },
    form: {
      handler(newForm) {
        this.$emit('config-change', { ...newForm });
      },
      deep: true
    }
  },
  validations() {
    return {
      form: {
        google_spreadsheet_id: { required },
        google_sheet_name: { required },
        google_credentials: { required },
      },
    };
  },
  methods: {
    async onTest() {
      const isValid = await this.validateForm();
      if (!isValid) return;
      
      this.$emit('config-change', this.buildPayload(true));
      
      this.isTesting = true;
      try {
        const testPayload = this.buildPayload(true);
        const { data } = await DataSyncConfigAPI.testConnection(testPayload);
        if (data.success) {
          useAlert(this.$t('AI_SETTINGS.DATA_SYNC.CONNECTION_SUCCESS'), {
            type: 'success',
          });
        } else {
          useAlert(data.message || this.$t('GENERAL_ERROR'), {
            type: 'error',
          });
        }
      } catch (e) {
        const errorMessage = e?.response?.data?.error || e?.response?.data?.message || this.$t('GENERAL_ERROR');
        useAlert(errorMessage, { type: 'error' });
      } finally {
        this.isTesting = false;
      }
    },
    async validateForm() {
      this.v$.$touch();
      return !this.v$.$invalid;
    },
    
    updateLayoutItem(index, field, value) {
      const updatedItems = [...this.layoutItems];
      updatedItems[index] = { ...updatedItems[index], [field]: value };
      this.$emit('layout-change', updatedItems);
    },
    buildPayload(includeCredentials = false) {
      const payload = {
        is_enabled: true, // Always enabled when Google Sheets is selected
        target: 'google_sheets', // Always Google Sheets
        google_spreadsheet_id: this.form.google_spreadsheet_id,
        google_sheet_name: this.form.google_sheet_name,
      };
      if (includeCredentials && this.form.google_credentials) {
        payload.google_credentials = this.form.google_credentials;
      }
      return payload;
    },
    isJson(text) {
      try {
        JSON.parse(text);
        return true;
      } catch (_) {
        return false;
      }
    },
    humanize(iso) {
      try {
        return new Date(iso).toLocaleString();
      } catch (_) {
        return iso;
      }
    },
    getRequiredFieldError(labelKey) {
      return this.$t('GENERAL.ERRORS.REQUIRED_FIELD', {
        field: this.$t(labelKey),
      });
    },
  },
};
</script>
